import PropTypes from "prop-types";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@shared/hooks";

/**
 * SuperAdminRoute - Route protection for super administrators
 *
 * This component checks for super admin access using Supabase's app_metadata
 * system, which is secure and cannot be modified by users.
 *
 * Key features:
 * - Checks user.app_metadata.is_super_admin flag
 * - No database queries needed (metadata is in user object)
 * - Secure: app_metadata can only be set via admin API
 * - Fast: No async operations required
 */
export const SuperAdminRoute = ({
  children,
  fallbackPath = "/admin/login",
}) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Check super admin status from app_metadata (secure, server-controlled)
  const isSuperAdmin = user?.app_metadata?.is_super_admin === true;

  // Redirect to unauthorized if authenticated but not super admin
  if (!isSuperAdmin) {
    return <Navigate to="/admin/unauthorized" replace />;
  }

  // Render children if authenticated and super admin
  return children;
};

SuperAdminRoute.propTypes = {
  children: PropTypes.node.isRequired,
  fallbackPath: PropTypes.string,
};
